import React, { Component } from 'react';
import { connect } from 'react-redux';
import { array, bool, func, number, object, shape, string } from 'prop-types';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import { Tooltip } from '@fiverr-private/fit';
import { getContext, I18n, I18nProvider } from '@fiverr-private/fiverr_context';
import SafePureComponent from '../shared/SafePureComponent';
import SellerLanguage from '../shared/SellerLanguage';
import RatingWrapper from '../shared/RatingWrapper';
import { REVIEWS } from '../../utils/pageSections';
import { validateOverview } from '../../utils/mainComponentsValidator';
import { isFiverrChoice } from '../../utils/fiverrChoice';
import { isRisingTalent } from '../../utils/risingTalent';
import { getCurrentContent } from '../../utils/ugc/getCurrentContent';
import { getSellerLanguageParams } from '../../utils/sellerLanguage';
import { BQ_CLICKED_REVIEWS_RATINGS } from '../../utils/bigQueryEvents';
import LoyaltySellerBanner from './LoyaltySellerBanner';
import OverviewProfileImage from './ProfileImage';
import CategoriesTreeBreadcrumbs from './CategoriesTreeBreadcrumbs';
import LazyFlag from './LazyFlag';

import './style.scss';

class GigOverview extends Component {

    constructor(props) {
        super(props);

        this.onReviewsClick = this.onReviewsClick.bind(this);
    }

    onReviewsClick() {
        const { biEvents } = this.context;

        biEvents.sendBigQueryEvent({ eventName: BQ_CLICKED_REVIEWS_RATINGS });
        this.props.scrollToSection({ sectionKey: REVIEWS });
    }

    showRisingTalentBadge() {
        const { general } = this.context;
        const { isHandpicked } = general;

        return isRisingTalent(isHandpicked);
    }

    showFiverrChoiceBadge() {
        const { choiceEligibilities } = this.context;
        const { queryParameters, abTests } = getContext();

        return isFiverrChoice({ queryParameters, abTests, choiceEligibilities });
    }

    showUserInfo() {
        const { ordersInQueue, ratingsCount } = this.props.overview.gig;
        const showOrdersInQueue = ordersInQueue > 0;
        const showRating = ratingsCount > 0;

        return showRating || showOrdersInQueue || this.showFiverrChoiceBadge() || this.showRisingTalentBadge();
    }

    showLoyaltySellerBanner() {
        const { repeatScore } = this.props;
        const { isMobile } = getContext();

        return !isEmpty(repeatScore) && !isMobile;
    }

    render() {
        const { overview, useStickyLayout, scrollToSection, studioInfo, gigTitle, shouldRenderSellerLanguage } = this.props;
        const { locale, browserLanguage, countryCode } = getContext();

        if (!validateOverview({ overview })) {
            return null;
        }

        const { gig, categories, seller } = overview;
        const { ordersInQueue, ratingsCount, rating } = gig;
        const showOrdersInQueue = ordersInQueue > 0;
        const showRating = ratingsCount > 0;
        const showFiverrChoiceBadge = this.showFiverrChoiceBadge();
        const showRisingTalentBadge = !showFiverrChoiceBadge && this.showRisingTalentBadge();
        const showUserInfo = this.showUserInfo();
        const showLoyaltySellerBanner = this.showLoyaltySellerBanner();
        const sellerLanguageParams = shouldRenderSellerLanguage ? getSellerLanguageParams({
            buyer: { locale, browserLanguage, countryCode },
            seller
        }) : null;

        const gigOverviewClasses = classNames(
            'gig-overview',
            { 'with-seller-language': shouldRenderSellerLanguage },
            { 'with-loyalty': showLoyaltySellerBanner }
        );

        return (
            <div className={gigOverviewClasses}>
                <CategoriesTreeBreadcrumbs categories={categories}/>
                <h1 className="text-display-3">{gigTitle}</h1>
                <div className="seller-overview">
                    <OverviewProfileImage seller={seller} studioInfo={studioInfo}
                        scrollToSection={scrollToSection}
                        useStickyLayout={useStickyLayout}/>
                    {showUserInfo &&
                        <div className="user-info">
                            {showRating &&
                            <RatingWrapper className="user-info-rating"
                                onClick={this.onReviewsClick}
                                rating={rating}
                                ratingsCount={ratingsCount}/>
                            }
                            {showOrdersInQueue &&
                                <span className="orders-in-queue">
                                    <I18n k="gig_page_perseus.overview.orders_in_queue" params={{ amount: ordersInQueue }}/>
                                </span>
                            }
                            {showFiverrChoiceBadge &&
                                <Tooltip className="gig-overview-tooltip"
                                    boxContentClassName="gig-overview-tooltip-box-content"
                                    containerClassName="gig-overview-tooltip-container"
                                    content={<I18n k="gig_page_perseus.overview.fiverr_choice"/>}
                                    position={Tooltip.POSITIONS.BOTTOM}>
                                    <span className="fiverr-choice"/>
                                </Tooltip>
                            }
                            {showRisingTalentBadge &&
                                <I18nProvider>
                                    {({ i18n }) => (
                                        <Tooltip className="gig-overview-tooltip"
                                            boxContentClassName="gig-overview-tooltip-box-content"
                                            containerClassName="gig-overview-tooltip-container"
                                            content={i18n.t('gig_page_perseus.overview.rising_talent.tooltip')}
                                            position={Tooltip.POSITIONS.BOTTOM}>
                                            <span className="rising-talent">
                                                <I18n k="gig_page_perseus.overview.rising_talent.caption"/>
                                            </span>
                                        </Tooltip>
                                    )}
                                </I18nProvider>
                            }
                        </div>
                    }
                    {shouldRenderSellerLanguage &&
                        <div className="seller-locale">
                            <span className="seller-country">
                                <LazyFlag countryCode={sellerLanguageParams.sellerCountryCode}/>
                                <I18n k={`countries.${sellerLanguageParams.sellerCountryCode}`}/>
                            </span>
                            <SellerLanguage sellerLanguages={sellerLanguageParams.sellerValidatedLanguages}
                                commonLanguage={sellerLanguageParams.commonLanguage}/>
                        </div>
                    }
                </div>
                { showLoyaltySellerBanner && <LoyaltySellerBanner sellerName={seller.username}/>}
            </div>
        );
    }
}

GigOverview.propTypes = {
    overview: shape({
        gig: shape({
            title: string.isRequired,
            rating: number,
            ratingsCount: number,
            ordersInQueue: number
        }),
        categories: shape({
            category: shape({
                name: string,
                slug: string
            }),
            subCategory: shape({
                name: string,
                slug: string
            }),
            nestedSubCategory: shape({
                name: string,
                slug: string
            })
        }),
        seller: shape({
            id: number.isRequired,
            username: string,
            achievement: number,
            isPro: bool,
            hasProfilePhoto: bool,
            profilePhoto: string
        })
    }),
    studioInfo: object,
    scrollToSection: func,
    useStickyLayout: bool,
    shouldRenderSellerLanguage: bool
};

GigOverview.defaultProps = {
    studioInfo: {}
};

GigOverview.contextTypes = {
    general: object,
    choiceEligibilities: array,
    biEvents: object
};

const mapStateToProps = ({ ugc }) => {
    const { gigTitle } = getCurrentContent({ ugc });

    return {
        gigTitle
    };
};

const ConnectedOverView = connect(mapStateToProps)(GigOverview);

export { GigOverview, ConnectedOverView };
export default SafePureComponent(ConnectedOverView);
